/**
 * Composition root for the CORTEX API client.
 *
 * Callers get one entry point — `createApiClient({ baseUrl })` — and a
 * grouped, typed surface: `client.health.check()`, future
 * `client.quotes.list()`, etc. Each group is built from a shared
 * `Transport`, so request-id propagation, error envelope parsing, and
 * schema validation are configured exactly once.
 */

import { createAuditClient, type AuditClient } from './auditClient.js';
import { createAuthClient, type AuthClient } from './authClient.js';
import { createEngineClient, type EngineClient } from './engineClient.js';
import { createEntitiesClient, type EntitiesClient } from './entitiesClient.js';
import {
  createFacilitiesAdminClient,
  type FacilitiesAdminClient,
} from './facilitiesAdminClient.js';
import { createHealthClient, type HealthClient } from './healthClient.js';
import { createQuotesClient, type QuotesClient } from './quotesClient.js';
import { createRolesClient, type RolesClient } from './rolesClient.js';
import { createSectorClient, type SectorClient } from './sectorClient.js';
import { createStatsClient, type StatsClient } from './statsClient.js';
import { createTransport, type TransportOptions } from './transport.js';
import { createUsersClient, type UsersClient } from './usersClient.js';

export interface ApiClient {
  readonly audit: AuditClient;
  readonly auth: AuthClient;
  readonly entities: EntitiesClient;
  readonly facilities: FacilitiesAdminClient;
  readonly users: UsersClient;
  readonly roles: RolesClient;
  readonly health: HealthClient;
  readonly quotes: QuotesClient;
  readonly engine: EngineClient;
  readonly sectors: SectorClient;
  readonly stats: StatsClient;
}

export const createApiClient = (options: TransportOptions): ApiClient => {
  const transport = createTransport(options);
  return {
    audit: createAuditClient(transport),
    auth: createAuthClient(transport),
    entities: createEntitiesClient(transport),
    facilities: createFacilitiesAdminClient(transport),
    users: createUsersClient(transport),
    roles: createRolesClient(transport),
    health: createHealthClient(transport),
    quotes: createQuotesClient(transport),
    engine: createEngineClient(transport),
    sectors: createSectorClient(transport),
    stats: createStatsClient(transport),
  };
};
